import { StylovyzeDialog, useGlobalization } from '@innovyze/stylovyze';
import { DialogProps, FormControl, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	selectRehabTreeIsRenaming,
	selectRehabTreesList,
	selectRehabTreesNameError,
} from '@Selectors/Rehab.selectors';
import { renameRehabTree, setRehabTreeNameError } from '@Actions/index';

interface Props {
	open: boolean;
	treeId: string;
	name: string;
	description: string;
	onClose: (saved: boolean, newName: string, newDescription: string) => void;
}

export const RenameRehab = ({
	open,
	treeId,
	name,
	description,
	onClose,
}: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const [newName, setNewName] = useState('');
	const [newDescription, setNewDescription] = useState('');
	const configNameError = selectRehabTreesNameError();
	const isRenaming = selectRehabTreeIsRenaming();
	const existingTrees = selectRehabTreesList();

	const handleCancelNewModel = () => {
		onClose(false, '', '');
	};

	useEffect(() => {
		dispatch(setRehabTreeNameError(''));
		setNewName(name);
		setNewName(description);
	}, [name, open]);

	useEffect(() => {
		if (!isRenaming && configNameError.length == 0) {
			onClose(true, newName, newDescription);
		}
	}, [isRenaming]);

	const handleRename = () => {
		if (configNameError.length == 0) {
			dispatch(
				renameRehabTree({
					treeId: treeId,
					name: newName,
					description: newDescription,
				}),
			);
		}
	};

	const handlelNameChangeValue = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		let error = '';
		if (event.target && event.target.value !== '') {
			setNewName(event.target.value);
			const duplicateName = existingTrees.filter(
				tree =>
					tree.name.toUpperCase().trim() ===
						event.target.value.toUpperCase().trim() &&
					tree._id !== treeId,
			);

			if (duplicateName.length > 0) {
				error = t('Enter a unique name');
			} else {
				const rexp = new RegExp('^[a-zA-Z0-9 ]+$');
				const test = rexp.test(event.target.value);
				if (!test) {
					error = t('Only alphanumeric characters are allowed');
				} else {
					const rexp2 = new RegExp('^.{1,50}$');
					const test2 = rexp2.test(event.target.value);
					if (!test2) {
						error = t(
							'Name cannot contain more than 50 characters',
						);
					}
				}
			}
		} else {
			setNewName('');
			error = t('Name is required');
		}
		dispatch(setRehabTreeNameError(error));
	};

	const handlelDescriptionChangeValue = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		if (event.target && event.target.value !== '') {
			setNewDescription(event.target.value);
		} else {
			setNewDescription('');
		}
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: open,
		fullWidth: true,
		maxWidth: 'sm',
		onClose: onCloseDialog,
	};

	return (
		<StylovyzeDialog
			dialogProps={dialogProps}
			data-cy="new-dialog"
			title={t('Rename Asset Group Set')}
			open={open}
			cancelText={t('Cancel')}
			cancelButtonProps={{
				disabled: isRenaming,
				dataCy: 'rename-cancel',
			}}
			onCancel={handleCancelNewModel}
			confirmText={t('OK')}
			confirmButtonProps={{
				disabled:
					isRenaming ||
					configNameError.length !== 0 ||
					(name !== undefined &&
						description !== undefined &&
						name.trim().toLowerCase() ===
							newName.trim().toLowerCase() &&
						description.trim().toLowerCase() ===
							newDescription.trim().toLowerCase()),
				dataCy: 'rename-confirm',
			}}
			onConfirm={handleRename}>
			<Grid container>
				<Grid item xs={12}>
					<FormControl variant="outlined" margin="normal" fullWidth>
						<TextField
							fullWidth
							defaultValue={name}
							error={configNameError.length !== 0}
							required
							name="name"
							label={t('Edit Name')}
							variant="outlined"
							onChange={handlelNameChangeValue}
							helperText={configNameError}
						/>
					</FormControl>
					<FormControl variant="outlined" margin="normal" fullWidth>
						<TextField
							fullWidth
							defaultValue={description}
							name="description"
							label={t('Edit Description')}
							variant="outlined"
							onChange={handlelDescriptionChangeValue}
						/>
					</FormControl>
				</Grid>
			</Grid>
		</StylovyzeDialog>
	);
};
